// frontend/src/pages/admin/RegisterModal.js
import { useState, useEffect } from "react";
import {
  adminCreateUser,
  getCustomersWithRegions,
} from "../../library/allauth";
import {
  UserPlus,
  CheckCircle,
  Mail,
  User,
  Shield,
  MapPin,
  Sliders,
  ChevronRight,
  Building2,
} from "lucide-react";

// ─── Role definitions ─────────────────────────────────────────────────────────
// NOTE: value must match the Django group name exactly
const ROLES = [
  {
    value: "customer_user",
    label: "Customer User",
    desc: "Standard portal access",
  },
  {
    value: "customer_admin",
    label: "Customer Admin",
    desc: "Manages users for their customer",
  },
  {
    value: "xroadz_admin",
    label: "Super Admin",
    desc: "Full admin privileges",
  },
];

const ALL_FEATURES = [
  { key: "analytics", label: "Analytics", desc: "Dashboard & KPI charts" },
  { key: "alpr", label: "ALPR", desc: "License plate recognition" },
  { key: "footage", label: "Footage", desc: "Video footage access" },
  { key: "map", label: "Map", desc: "Interactive map view" },
];

export default function RegisterModal({
  onSuccess,
  onCancel,
  currentUserRole,
}) {
  // customers = [{ id, name, regions: [{id, name}] }]
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: "customer_user",
    customer_id: null, // selected customer
    region_ids: [],
    features: {
      analytics: true,
      alpr: false,
      footage: true,
      map: true,
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Fetch customers (with their regions) on mount
  useEffect(() => {
    if (currentUserRole === null) return; // wait until role is known
    setLoadingCustomers(true);
    getCustomersWithRegions()
      .then((res) => {
        if (res.status === 200) {
          setCustomers(res.data);
          // Customer Admin: auto-select their only customer
          if (currentUserRole !== "xroadz_admin" && res.data.length === 1) {
            setForm((f) => ({ ...f, customer_id: res.data[0].id }));
          }
        }
      })
      .finally(() => setLoadingCustomers(false));
  }, [currentUserRole]); // re-run when role is known

  // Regions available for the currently selected customer
  const availableRegions = form.customer_id
    ? (customers.find((c) => c.id === form.customer_id)?.regions ?? [])
    : [];

  // When customer changes, clear selected regions
  const handleCustomerChange = (customerId) => {
    setForm((f) => ({ ...f, customer_id: customerId, region_ids: [] }));
  };

  const toggleRegion = (id) => {
    setForm((f) => ({
      ...f,
      region_ids: f.region_ids.includes(id)
        ? f.region_ids.filter((r) => r !== id)
        : [...f.region_ids, id],
    }));
  };

  const toggleFeature = (key) => {
    setForm((f) => ({
      ...f,
      features: { ...f.features, [key]: !f.features[key] },
    }));
  };

  const selectAllRegions = () =>
    setForm((f) => ({ ...f, region_ids: availableRegions.map((r) => r.id) }));
  const clearRegions = () => setForm((f) => ({ ...f, region_ids: [] }));

  const handleSubmit = async () => {
    setError("");
    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!form.customer_id && form.role !== "xroadz_admin") {
      setError("Please select a customer.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        // Super Admin has no customer restriction
        customer_id: form.role === "xroadz_admin" ? null : form.customer_id,
      };
      const res = await adminCreateUser(payload);
      if (res.status === 201) {
        setDone(true);
      } else {
        setError(res.error || "Something went wrong.");
      }
    } catch {
      setError("Request failed.");
    } finally {
      setLoading(false);
    }
  };

  // ── Success State ──────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="max-w-lg mx-auto mt-20 text-center px-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm">
          <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            User Created!
          </h2>
          <p className="text-gray-500 text-sm mb-8">
            An invite email has been sent to{" "}
            <strong className="text-gray-700">{form.email}</strong>. They'll set
            their password via the link.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setForm({
                  first_name: "",
                  last_name: "",
                  email: "",
                  role: "customer_user",
                  customer_id: null,
                  region_ids: [],
                  features: {
                    analytics: true,
                    alpr: false,
                    footage: true,
                    map: true,
                  },
                });
                setDone(false);
              }}
              className="px-5 py-2.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Register Another
            </button>
            <button
              onClick={onSuccess}
              className="px-5 py-2.5 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors flex items-center gap-2"
            >
              Back to Users <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Whether the currently logged-in admin is a Super Admin
  // (passed from AdminPortal via prop — Super Admin can pick any customer;
  //  Customer Admin sees only their own customer pre-selected)
  const isSuperAdmin = currentUserRole === "xroadz_admin";

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto px-2">
      {/* Page Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <UserPlus size={22} className="text-emerald-700" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Register New User
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Fill in the details below. An invite email will be sent
            automatically.
          </p>
        </div>
        <div className="ml-auto flex gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-60"
          >
            <Mail size={15} />
            {loading ? "Creating..." : "Create & Send Invite"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
          <Shield size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {/* 3 Column Layout */}
      <div className="grid grid-cols-3 gap-6">
        {/* Card 1 — Basic Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
              <User size={14} className="text-gray-500" />
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Basic Info
            </p>
          </div>

          <div className="space-y-4">
            {/* First / Last Name */}
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">
                First Name
              </label>
              <input
                value={form.first_name}
                onChange={(e) =>
                  setForm({ ...form, first_name: e.target.value })
                }
                placeholder="Jane"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">
                Last Name
              </label>
              <input
                value={form.last_name}
                onChange={(e) =>
                  setForm({ ...form, last_name: e.target.value })
                }
                placeholder="Doe"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">
                Email <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Mail
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@cambridge.gov"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                <Shield size={11} /> Becomes their username
              </p>
            </div>

            {/* Role */}
            <div>
              <label className="text-xs text-gray-500 mb-2 block">Role</label>
              <div className="space-y-2">
                {ROLES
                  // Customer Admin cannot create Super Admins
                  .filter((r) => {
                    if (isSuperAdmin) return true;
                    // Customer Admin can only create Customer User
                    return r.value === "customer_user";
                  })
                  .map((r) => (
                    <label
                      key={r.value}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        form.role === r.value
                          ? "border-emerald-300 bg-emerald-50"
                          : "border-gray-100 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={r.value}
                        checked={form.role === r.value}
                        onChange={() => setForm({ ...form, role: r.value })}
                        className="accent-emerald-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {r.label}
                        </p>
                        <p className="text-xs text-gray-400">{r.desc}</p>
                      </div>
                    </label>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Card 2 — Customer & Region Access */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
              <Building2 size={14} className="text-gray-500" />
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Customer & Regions
            </p>
          </div>

          {/* Hide customer picker for Super Admin role */}
          {form.role === "xroadz_admin" ? (
            <div className="py-6 text-center">
              <Shield size={28} className="text-gray-200 mx-auto mb-2" />
              <p className="text-xs text-gray-400">
                Super Admins have access to all customers and regions.
              </p>
            </div>
          ) : (
            <>
              {/* Customer Dropdown */}
              <div className="mb-4">
                <label className="text-xs text-gray-500 mb-1.5 block">
                  Customer <span className="text-red-400">*</span>
                </label>
                {loadingCustomers ? (
                  <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-400">
                    Loading customers...
                  </div>
                ) : (
                  <select
                    value={form.customer_id ?? ""}
                    onChange={(e) =>
                      handleCustomerChange(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                    // Customer Admin is locked to their own customer — disable picker
                    disabled={!isSuperAdmin}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">Select a customer...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Regions — only shown once a customer is selected */}
              {form.customer_id && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                      <MapPin size={12} /> Regions
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAllRegions}
                        className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                      >
                        All
                      </button>
                      <span className="text-gray-300 text-xs">|</span>
                      <button
                        onClick={clearRegions}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    Select which regions this user can access.
                  </p>
                  <div className="space-y-1">
                    {availableRegions.map((region) => {
                      const checked = form.region_ids.includes(region.id);
                      return (
                        <label
                          key={region.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            checked
                              ? "border-emerald-200 bg-emerald-50"
                              : "border-transparent hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRegion(region.id)}
                            className="w-4 h-4 accent-emerald-600"
                          />
                          <div className="flex items-center gap-2">
                            <MapPin
                              size={13}
                              className={
                                checked ? "text-emerald-600" : "text-gray-300"
                              }
                            />
                            <span
                              className={`text-sm font-medium ${checked ? "text-emerald-800" : "text-gray-600"}`}
                            >
                              {region.name}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {form.region_ids.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-400">
                        <span className="font-semibold text-emerald-700">
                          {form.region_ids.length}
                        </span>{" "}
                        region{form.region_ids.length > 1 ? "s" : ""} selected
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Card 3 — Feature Access */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
              <Sliders size={14} className="text-gray-500" />
            </div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Feature Access
            </p>
          </div>
          <p className="text-xs text-gray-400 mb-5">
            Toggle which features this user can access in the portal.
          </p>
          <div className="space-y-3">
            {ALL_FEATURES.map((feature) => {
              const enabled = form.features[feature.key];
              return (
                <div
                  key={feature.key}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    enabled
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-gray-100 bg-gray-50"
                  }`}
                >
                  <div>
                    <p
                      className={`text-sm font-medium ${enabled ? "text-emerald-800" : "text-gray-500"}`}
                    >
                      {feature.label}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {feature.desc}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleFeature(feature.key)}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                      enabled ? "bg-emerald-600" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        enabled ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              <span className="font-semibold text-emerald-700">
                {Object.values(form.features).filter(Boolean).length}
              </span>{" "}
              of {ALL_FEATURES.length} features enabled
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
